import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppConfigService } from '../../../services/config/app-config.service';
import { TokenStorageService } from '../../../services/storage/token-storage.service';
import { environment } from '../../../../environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-invest-api',
    templateUrl: './invest-api.component.html',
    styleUrl: './invest-api.component.css',
    standalone: false
})
export class InvestApiComponent implements OnInit, OnDestroy {
  userType: string | undefined;
  iframeUrl: SafeResourceUrl | null = null;

  private messageListener!: (event: MessageEvent) => void;
  private routeSub?: Subscription;
  private suppressNextIframeSync = false;
  private lastIframePath = '';
  private readonly iframeOrigin = new URL(
    environment.investApiIframeBaseUrl
  ).origin;

  constructor(
    private appConfig: AppConfigService,
    private tokenStorageService: TokenStorageService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      environment.investApiIframeUrl
    );

    const normalizeChildPath = (raw: string) => {
      let path = raw || '';
      let fragment: string | undefined;
      const hashIdx = path.indexOf('#');
      if (hashIdx >= 0) {
        fragment = path.slice(hashIdx + 1);
        path = path.slice(0, hashIdx);
      }

      path = path.replace(/^\/+/, '');
      // Strip iframe base path: "/invest/docs/..." -> "/docs/..." (child sends full path)
      path = path.replace(/^invest\/+/, '');
      path = path.replace(/^docs\/+/, '');
      path = path.replace(/^doc\/invest\/+/, '');
      path = path.replace(/\/{2,}/g, '/');

      return { path, fragment };
    };

    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== this.iframeOrigin) return;

      const data: any = event.data;

      // Handle error signals from Docusaurus (404 / 403)
      if (data && typeof data === 'object' && data.type === 'GTN_ERROR') {
        const code = data.code === 403 ? 403 : 404;
        this.router.navigate([`/${code}`]);
        return;
      }

      const posted: string | undefined = data?.path;
      if (!posted) return;

      const { path, fragment } = normalizeChildPath(posted);

      // Guard: ignore "unchanged" - Docusaurus sends this as fallback when version
      // resolution fails; navigating to it would overwrite the correct URL
      if (path === 'unchanged' || path?.startsWith('unchanged/')) {
        return;
      }

      const segments = path
        ? ['doc', 'invest', ...path.split('/').filter(Boolean)]
        : ['doc', 'invest'];
      this.suppressNextIframeSync = true;
      this.router.navigate(segments, { replaceUrl: true, fragment });
    };

    window.addEventListener('message', this.messageListener, false);

    this.routeSub = this.route.url.subscribe(() => {
      const tree = this.router.parseUrl(this.router.url);
      const fragment = tree.fragment || '';

      // Read only the primary route segments so query params and hash fragments
      // never leak into the iframe path (this.router.url contains them).
      const primarySegments =
        tree.root.children['primary']?.segments.map((s) => s.path) || [];
      const docIndex = primarySegments.findIndex(
        (seg, i) => seg === 'doc' && primarySegments[i + 1] === 'invest',
      );
      const child =
        docIndex === -1 ? '' : primarySegments.slice(docIndex + 2).join('/');
      let iframePath =
        child.length === 0
          ? '/'
          : (child.startsWith('changelog') ? '/' : '/docs/') + child;

      // Bare version ("1.0.0") keeps a trailing slash: CloudFront treats a
      // dotted last segment as a file and would serve the SPA fallback instead
      // of the folder's index.html (the gtn-redirects entry page).
      if (/^\/docs\/\d+\.\d+(?:\.\d+)?$/.test(iframePath)) {
        iframePath += '/';
      }

      iframePath = iframePath.replace(/\/{2,}/g, '/');
      const finalPath = fragment ? `${iframePath}#${fragment}` : iframePath;

      if (this.suppressNextIframeSync) {
        this.suppressNextIframeSync = false;
        return;
      }

      if (finalPath !== this.lastIframePath) {
        this.lastIframePath = finalPath;
        const url = environment.investApiIframeBaseUrl + finalPath;
        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener, false);
    this.routeSub?.unsubscribe();
  }
}
